import { createApi } from '@reduxjs/toolkit/query/react';
import { AgentType } from '../../models/agent';
import { IPagedResult, ISearchCriteria } from '../../models/filter';
import { IRoleReadModel } from '../../models/role';
import { IConnectedUserModel, IUserReadModel, IUserWriteModel } from '../../models/user';
import { buildArrayContainsQueryString, buildQueryString } from '../../utils/mongoFilterUtils';
import { createRbacBaseQuery, del, pagedResultToCacheTags, patch, post, put } from '../../utils/helpers';

function buildUserOnlyQueryString(searchCriteria: ISearchCriteria): string {
    const filters = [{ not: true, column: 'Scopes.Name', value: 'bp.access' }];

    return buildQueryString(searchCriteria, ['Name', 'EmailAddress', 'PhoneNumber'], filters);
}

function buildAgentQueryString(searchCriteria: ISearchCriteria, agentType?: AgentType): string {
    const filters = [
        { column: 'Scopes.Name', value: 'bp.access' },
        ...(agentType ? [{ column: 'Scopes.Claims.Value', value: agentType }] : []),
    ];

    return buildQueryString(searchCriteria, ['Name', 'EmailAddress', 'PhoneNumber'], filters);
}

const userApi = createApi({
    reducerPath: 'user',
    tagTypes: ['User', 'UserList', 'AgentList', 'UserRole', 'ConnectedUsers'],
    baseQuery: createRbacBaseQuery(''),
    endpoints: (builder) => ({
        // Users
        searchUsers: builder.query<IPagedResult<IUserReadModel>, ISearchCriteria>({
            query: (searchCriteria) => `/users?${buildUserOnlyQueryString(searchCriteria)}`,
            providesTags: (res) => pagedResultToCacheTags(res, 'User', 'UserList'),
        }),
        searchAgents: builder.query<IPagedResult<IUserReadModel>, { searchCriteria: ISearchCriteria, agentType?: AgentType }>({
            query: ({ searchCriteria, agentType }) => `/users?${buildAgentQueryString(searchCriteria, agentType)}`,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            providesTags: (res) => [...pagedResultToCacheTags(res, 'User', 'UserList'), 'AgentList'] as any,
        }),
        getUserById: builder.query<IUserReadModel, string>({
            query: (userId) => `/users/${userId}`,
            providesTags: (_, __, userId) => [{ type: 'User', id: userId }],
        }),
        getUsersByIds: builder.query<IPagedResult<IUserReadModel>, string[]>({
            query: ids => `/users?${buildArrayContainsQueryString('_id', ids)}`,
            providesTags: (res) => res?.results.map(({ id }) => ({ type: 'User', id })) || [],
        }),
        createUser: builder.mutation<IUserReadModel, IUserWriteModel>({
            query: (user) => post('/users', user),
            invalidatesTags: ['UserList'],
        }),
        updateUser: builder.mutation<IUserReadModel, { userId: string, user: IUserWriteModel }>({
            query: ({ userId, user }) => put(`/users/${userId}`, user),
            invalidatesTags: (_, __, args) => [{ type: 'User', id: args.userId }],
        }),
        demoteAgentToUser: builder.mutation<IUserReadModel, IUserReadModel>({
            query: (user) => put(`/users/${user.id}`, { ...user, scopes: user.scopes?.filter(s => s.name !== 'bp.access') }),
            invalidatesTags: (_, __, { id }) => [{ type: 'User', id }, 'AgentList', 'UserList'],
        }),
        setUserEnabled: builder.mutation<IUserReadModel, { userId: string, enabled: boolean }>({
            query: ({ userId, enabled }) => patch(`/users/${userId}?enabled=${enabled}`),
            invalidatesTags: (_, __, args) => [{ type: 'User', id: args.userId }],
        }),
        deleteUser: builder.mutation<void, string>({
            query: (userId) => del(`/users/${userId}`),
            invalidatesTags: ['UserList'],
        }),
        resendInvitation: builder.mutation<void, string>({
            query: (userId) => `/users/${userId}/resend`,
        }),
        batchCreateUser: builder.mutation<IUserReadModel[], IUserWriteModel[]>({
            query: (users) => post('/usersbatch', users),
            invalidatesTags: ['UserList'],
        }),
        getConnectedUsers: builder.query<IConnectedUserModel[], string>({
            query: (userId) => `/users/${userId}/connectedUsers`,
            providesTags: (_, __, userId) => [{ type: 'ConnectedUsers', id: userId }],
        }),
        // User roles
        getRoleAssignedToUser: builder.query<IRoleReadModel, string>({
            query: (userId) => `/users/${userId}/role`,
            providesTags: (_, __, userId) => [{ type: 'UserRole', id: userId }],
        }),
        assignRoleToUser: builder.mutation<IRoleReadModel, { userId: string, roleId: number }>({
            query: ({ userId, roleId }) => post(`/users/${userId}/role/${roleId}`, {}),
            invalidatesTags: (_, __, args) => [{ type: 'UserRole', id: args.userId }],
        }),
        revokeRoleFromUser: builder.mutation<void, string>({
            query: (userId) => del(`/users/${userId}/role`),
            invalidatesTags: (_, __, userId) => [{ type: 'UserRole', id: userId }],
        }),
    }),
});

export default userApi;

export const {
    useSearchUsersQuery,
    useSearchAgentsQuery,
    useGetUserByIdQuery,
    useGetUsersByIdsQuery,
    useCreateUserMutation,
    useUpdateUserMutation,
    useDemoteAgentToUserMutation,
    useSetUserEnabledMutation,
    useDeleteUserMutation,
    useResendInvitationMutation,
    useBatchCreateUserMutation,
    useGetConnectedUsersQuery,
    useGetRoleAssignedToUserQuery,
    useAssignRoleToUserMutation,
    useRevokeRoleFromUserMutation,
} = userApi;